import * as React from "react";

import { cn } from "@/engine/support/classNames";

import { AnimalFigure } from "./AnimalFigure";
import {
  ANIMALS,
  type AnimalId,
  type AnimalNames,
  type ColourId,
} from "./campData";

export interface AnimalPickerProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onSelect"
> {
  value: AnimalId;
  colour?: ColourId | undefined;
  size?: "sm" | "md" | undefined;
  onSelect: (animal: AnimalId) => void;
  names: AnimalNames;
}

/** Canonical grid of the twelve camp animals, sized identically everywhere. */
export const AnimalPicker = React.forwardRef<HTMLDivElement, AnimalPickerProps>(
  function AnimalPicker(
    {
      value,
      colour = "ash",
      size = "md",
      onSelect,
      names,
      className,
      ...props
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        role="radiogroup"
        className={cn(
          "grid grid-cols-[repeat(auto-fill,minmax(5.25rem,1fr))] gap-2",
          className,
        )}
        {...props}
      >
        {ANIMALS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={value === item.id}
            aria-label={names[item.id]}
            onClick={() => onSelect(item.id)}
            className="group grid min-w-0 grid-rows-[1fr_auto] place-items-center gap-1.5 rounded-[10px] border border-raise bg-background p-2 transition-colors hover:border-primary/45 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none aria-checked:border-primary aria-checked:bg-primary/8"
          >
            <AnimalFigure
              animal={item.id}
              colour={colour}
              className={cn("w-full", size === "sm" ? "h-[72px]" : "h-[84px]")}
            />
            <span className="w-full truncate text-center text-xs leading-tight text-muted-foreground group-aria-checked:text-foreground">
              {names[item.id]}
            </span>
          </button>
        ))}
      </div>
    );
  },
);
